import { scaleBand, scaleLinear } from "d3";
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager";
import { CPPOptData, CPPOptPlotConfig } from "../../../../Types/CPPOptPlot";
import { MarginedBoundingBox } from "../../../../Types/MarginedBoundingBox";
import { Offset } from "../../../../Types/Offset";
import { CPPOptPlotReactCallbacks } from "../../../../Types/ReactCallbacks";
import { LEFT_MARGIN } from "../../Constants";
import { D3TimeBasedVisualization } from "../../D3TimeBasedVisualization";
import { D3CPPOptPlotConfigurationBuilder } from "./D3CPPOptPlotConfigurationBuilder";
import { D3CPPOptPlotRenderer } from "./D3CPPOptPlotRenderer";
import { range } from "lodash";
import { ModalityDataSource } from "../../../../Types/ModalityDataSource";
import { ModalityPage } from "../../../../Data/ModalityPage";
import { DataSource } from "../../../../Types/DataSource";
import { TraceDataConfig } from "../../../../Types/Trace";
import { Analytic, CPPOptAnalysis, CPPOptFlexAnalysis, PRxAnalysis } from "../../../../Types/AnalysisDetails";

export class D3CPPOptPlot extends D3TimeBasedVisualization<CPPOptPlotConfig, CPPOptPlotReactCallbacks, D3CPPOptPlotRenderer, TimeSeriesPageManager<ModalityPage>> {
    public xScaleBand = scaleBand()
    public xScaleLinear = scaleLinear().domain([40, 120])
    public cppoptBoxPlotYScale = scaleLinear().domain([40, 120]).clamp(true)
    public prxBoxPlotYScale = scaleLinear().domain([-1, 1]).clamp(true)
    public yScale = scaleLinear().domain([-1, 1])
    public pageManager: TimeSeriesPageManager<any>
    public bins: number[] = []
    public curveFitBoundingBox: MarginedBoundingBox
    public summaryValuesBoundingBox: MarginedBoundingBox
    public cppoptBoxPlotBoundingBox: MarginedBoundingBox
    public prxBoxPlotBoundingBox: MarginedBoundingBox

    private binMinimum = 42.5
    private binMaximum = 117.5
    private binSize = 5
    private curveFitMargins: Offset = { top: 65, left: LEFT_MARGIN, bottom: 132, right: 0 }

    public cppOptData: CPPOptData = {
        CPPOpt: null,
        LLA: null,
        ULA: null,
        includedDataPercentage: null,
        fitType: null,
        fitSpan: null,
        yValues: new Array(this.bins.length).fill(null),
        errors: new Array(this.bins.length).fill(null),
        includedInFit: new Array(this.bins.length).fill(null),
        dataPercentages: new Array(this.bins.length).fill(null),
        coefficients: [null, null, null],
        weight: null,
        startTime: null,
        endTime: null
    }

    constructor(root: HTMLDivElement, config: CPPOptPlotConfig, pageManager: TimeSeriesPageManager<any>, reactCallbacks: CPPOptPlotReactCallbacks) {
        super(root, config, pageManager, reactCallbacks)
        this.pageManager = pageManager
        this.bins = range(this.binMinimum - this.binSize/2, this.binMaximum + 3*this.binSize/2, this.binSize)

        this.curveFitBoundingBox = new MarginedBoundingBox(
            { width: 3*config.dimensions.width/8, height: config.dimensions.height }, 
            this.curveFitMargins
        )

        this.cppoptBoxPlotBoundingBox = new MarginedBoundingBox(
            { width: config.dimensions.width/4, height: config.dimensions.height },
            { top: 10, left: 40, bottom: 40, right: 10 }
        )

        this.prxBoxPlotBoundingBox = new MarginedBoundingBox(
            { width: config.dimensions.width/4, height: config.dimensions.height },
            { top: 10, left: 40, bottom: 40, right: 10 }
        )
        
        this.summaryValuesBoundingBox = new MarginedBoundingBox(
            { width: config.dimensions.width/2, height: config.dimensions.height },
            { top: 65, left: 10, bottom: 40, right: 10 }
        )

        this.mount(new D3CPPOptPlotRenderer(this, new D3CPPOptPlotConfigurationBuilder(this), "d3-cppopt-plot"))
    }

    public getCPPoptAnalysis(): CPPOptAnalysis | CPPOptFlexAnalysis {
        return  {
            analytic: this.config.cppoptAlgorithm,
            ABP: this.config.abpModality,
            ICP: this.config.icpModality,
            calculationWindowMs: this.config.cppoptAlgorithm === Analytic.CPPOpt ? 4 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000,
            calculationPeriodMs: 60 * 1000,
            prxThreshold: this.config.prxThreshold
        }
    }

    public getPRxAnalysis(): PRxAnalysis {
        return {
            analytic: Analytic.PRx,
            ABP: this.config.abpModality,
            ICP: this.config.icpModality,
            calculationWindowMs: 5 * 60 * 1000,
            calculationPeriodMs: 60 * 1000,
        }
    }

    public getVisibleTraces(): TraceDataConfig[] {
        return []
    }

    public getModalityDataSources(): ModalityDataSource[] {
        const dataObjectId = this.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number

        return [
            {
                modality: "CPPOpt,Composite,SampleSeries",
                dataObjectId,
                onDemandAnalysis: this.getCPPoptAnalysis()
            },
            {
                modality: "PRx",
                dataObjectId,
                onDemandAnalysis: this.getPRxAnalysis()
            }
        ]
    }

    public onYAxisDragStart = () => {
        this.renderer?.onYAxisDragStart()
    }

    public onYAxisDrag = () => {
		this.renderer?.onYAxisDrag()
	}

    public onYAxisDragEnd = () => {
        this.renderer?.onYAxisDragEnd()
    }

    public onXAxisDragStart = () => {
        this.renderer?.onXAxisDragStart()
    }

    public onYAxisDoubleClick = () => {
        this.renderer?.onYAxisDoubleClick()
    }

    public onXAxisDrag = () => {
		this.renderer?.onXAxisDrag()
	}

    public onXAxisDragEnd = () => {
        this.renderer?.onXAxisDragEnd()
    }

    public onXAxisDoubleClick = () => {
        this.renderer?.onXAxisDoubleClick()
    }

    public autoScale = () => {
        this.renderer?.autoScale()
    }
    
    public getXScaleLabels() {
        return this.bins.slice(0, -1).map((bin, index) => `${bin}-${this.bins[index + 1]}`)
    }
    
    protected renderPage(page: ModalityPage): void {
        this.renderer?.renderPage(page)
    }

    protected updateDerivedState(): void {
        const timelineHeight = 70
        const titleSpace = 20
        this.curveFitBoundingBox.setDimensions({ width: 3*this.config.dimensions.width/8, height: this.config.dimensions.height })
        this.summaryValuesBoundingBox.setDimensions({ width: this.config.dimensions.width/2, height: 3*this.config.dimensions.height/4 - timelineHeight - titleSpace})
        this.cppoptBoxPlotBoundingBox.setDimensions({ width: this.config.dimensions.width/4, height: 3*this.config.dimensions.height/4 - timelineHeight - titleSpace })
        this.prxBoxPlotBoundingBox.setDimensions({ width: this.config.dimensions.width/4, height: 3*this.config.dimensions.height/4 - timelineHeight - titleSpace })
        // The view scale controls the number of points that we render. 
        // So, setting a minimum of 1440 points here, which is full resolution for a modality sampled once per minute at a page duration of 1 day. 
        this.config.viewScale.range([0, Math.max(this.curveFitBoundingBox.width, 1440)])

        this.yScale.range([this.curveFitBoundingBox.height, 0])

        this.xScaleBand
            .domain(this.getXScaleLabels())
            .range([0, this.curveFitBoundingBox.width])

        this.xScaleLinear
            .range([0, this.curveFitBoundingBox.width])

        this.cppoptBoxPlotYScale.range([this.cppoptBoxPlotBoundingBox.height, 0])
        this.prxBoxPlotYScale.range([this.prxBoxPlotBoundingBox.height, 0])

        this.renderer?.updateChildren()
    }
}